import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import { InsuranceProgramDetailsDialog } from 'entities/insurance'

import { InsuranceApi } from 'shared/api/insurance'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { EmptyPage } from 'shared/ui/empty-page'

import { useInsuranceDraft } from '../../model/insurance-draft'
import { InsurancePolicyholderKind, InsuranceProgram } from '../../model/types'
import { InsuranceLayout } from '../../ui/insurance-layout'
import { PolicyholderChoiceDialog } from '../../ui/policyholder-choice-dialog'

import { ProgramOption } from './ui/program-option'
import { ProgramSkeleton } from './ui/program-skeleton'

import {
  mapInsuranceCalculationProgram,
  mapInsuranceDraftToCalculationRequest,
} from './model/program-calculation'
import s from './styles.module.scss'

const ProgramInsurancePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { openDialog, closeDialog } = useDialogBottomSheet()
  const { draft, setPolicyholderKind, setProgram } = useInsuranceDraft()
  const [programs, setPrograms] = useState<InsuranceProgram[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const hasSelectedProgram = programs.some(({ id }) => id === draft.program?.id)
  const travelersCount = draft.travelerAges.length + draft.infantAges.length
  const { country, purpose, startDate, endDate, travelerAges, infantAges } =
    draft

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const abortController = new AbortController()

    const loadPrograms = async () => {
      setIsLoading(true)
      setHasError(false)

      try {
        const payload = mapInsuranceDraftToCalculationRequest({
          country,
          purpose,
          startDate,
          endDate,
          travelerAges,
          infantAges,
        })
        const response = payload
          ? await InsuranceApi.calculatePrograms(
              payload,
              abortController.signal,
            )
          : null

        if (!abortController.signal.aborted) {
          const nextPrograms =
            response?.data.data.map(mapInsuranceCalculationProgram) ?? []
          setPrograms(nextPrograms)
        }
      } catch {
        if (!abortController.signal.aborted) {
          setHasError(true)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    loadPrograms()

    return () => abortController.abort()
  }, [country, endDate, infantAges, purpose, retryKey, startDate, travelerAges])

  useEffect(() => {
    if (!programs.length) return

    const nextProgram =
      programs.find(({ id }) => id === draft.program?.id) ||
      programs.find(({ isRecommended }) => isRecommended) ||
      programs[0]

    if (draft.program !== nextProgram) setProgram(nextProgram)
  }, [draft.program, programs, setProgram])

  const goToPolicyholder = () => {
    navigate(RoutingPaths.insurance.policyholder.path())
  }

  const onSelectPolicyholder = (kind: InsurancePolicyholderKind) => {
    setPolicyholderKind(kind)
    closeDialog()
    goToPolicyholder()
  }

  const onContinue = () => {
    if (draft.policyholderKind) {
      goToPolicyholder()
      return
    }

    openDialog({
      child: <PolicyholderChoiceDialog onSelect={onSelectPolicyholder} />,
      isDialog: false,
      isClickable: false,
      borderRadius: 20,
      padding: [20],
    })
  }

  const onShowDetails = (program: InsuranceProgram) => {
    openDialog({
      child: (
        <InsuranceProgramDetailsDialog
          title={program.name}
          description={program.description}
          onClose={closeDialog}
        />
      ),
      backgroundColor: 'var(--white)',
      maxHeight: '100dvh',
    })
  }

  return (
    <InsuranceLayout
      title={t('insurance.vzr.program.title')}
      onGoBack={() => navigate(-1)}
      footer={
        !isLoading && !hasError
          ? {
              disabled: !hasSelectedProgram,
              onClick: onContinue,
              children: t('insurance.vzr.actions.continue'),
            }
          : undefined
      }
    >
      {isLoading ? (
        <ProgramSkeleton />
      ) : hasError ? (
        <div className="container">
          <EmptyPage
            title={t('insurance.vzr.errors.load_title')}
            subTitle={t('error_des')}
            buttonText={t('try_again')}
            onClick={() => setRetryKey((current) => current + 1)}
          />
        </div>
      ) : (
        <div className={s.content}>
          <div className={s.notice}>
            <AppIcon
              className={s.noticeIcon}
              id={AllIcons.INFO_TRIANGLE}
              width={18}
              height={18}
            />
            <div className={s.noticeContent}>
              <p className={clsx(s.noticeTitle, 'bold-14')}>
                {t('insurance.vzr.program.label')}
              </p>
              <p className={clsx(s.noticeDescription, 'regular-12')}>
                {t('insurance.vzr.program.subtitle', {
                  count: travelersCount,
                })}
              </p>
            </div>
          </div>
          <div className={s.list}>
            {programs.map((program) => (
              <ProgramOption
                key={program.id}
                program={program}
                selected={draft.program?.id === program.id}
                onSelect={setProgram}
                onShowDetails={onShowDetails}
              />
            ))}
          </div>
        </div>
      )}
    </InsuranceLayout>
  )
}

export default ProgramInsurancePage
